import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import useSWR from "swr";

import { useMemo } from "react";

import classNames from "classnames";

import CustomCard from "components/CustomCard";
import UnverifiedPhoneNumber from "components/DiscussionRestrictionState/UnverifiedPhone";
import { profileUrl } from "components/ProfilePageLayout";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";

const SignUpForm = dynamic(() => import("components/SignUpForm"));

const ProfilePageDiscussionsList = dynamic(
  () => import("./ProfilePageDiscussionsList")
);

type ProfilePageDiscussionsProps = {};

const ProfilePageDiscussions = ({}: ProfilePageDiscussionsProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;

  const { authenticated, account } = useAuthentication();

  const { data: phoneNumber, isValidating: isPhoneNumberValidating } = useSWR(
    account?.id ? `/api/v2/accounts/${account?.id}/phone-number` : null,
    baseFetcher
  );

  const isCompany = router.route.startsWith("/companies/[slug]");

  const { data: profile } = useApi(profileUrl(slug, isCompany));
  const { name, companyName } = profile || {};

  const title = `${name}${
    companyName ? ` - ${companyName}` : ""
  } - Discussions`;
  const description = "Sign up to view latest discussions on Smartkarma";

  const content = useMemo(() => {
    if (
      account?.isPreviewPass &&
      !isPhoneNumberValidating &&
      !(phoneNumber?.verified || phoneNumber?.skipVerification)
    ) {
      return (
        <CustomCard
          className={classNames(
            "mx-0 mt-0 rounded-none",
            "md:rounded-xl md:mt-[45px]",
            "min-h-[max(calc(100vh-480px),720px)]"
          )}
        >
          <UnverifiedPhoneNumber />
        </CustomCard>
      );
    }

    if (!account?.canViewComment) {
      if (account?.company?.disableViewingComments) {
        return (
          <CustomCard
            className={classNames(
              "mx-0 mt-0 rounded-none",
              "md:rounded-xl md:mt-[45px]",
              "min-h-[max(calc(100vh-480px),720px)]"
            )}
          >
            <i>
              Your Compliance Team has restricted your access to this feature.
            </i>
          </CustomCard>
        );
      } else {
        <CustomCard
          className={classNames(
            "mx-0 mt-0 rounded-none",
            "md:rounded-xl md:mt-[45px]",
            "min-h-[max(calc(100vh-480px),720px)]"
          )}
        >
          <i>
            As a {account?.accountType} holder, you do not have access to
            discussions
          </i>
        </CustomCard>;
      }
    }

    return (
      <CustomCard
        className={classNames(
          "mx-0 mt-0 rounded-none",
          "md:rounded-xl md:mt-[45px]",
          "min-h-[max(calc(100vh-480px),720px)]"
        )}
      >
        <ProfilePageDiscussionsList />
      </CustomCard>
    );
  }, [
    account?.accountType,
    account?.canViewComment,
    account?.company?.disableViewingComments,
    account?.isPreviewPass,
    isPhoneNumberValidating,
    phoneNumber?.skipVerification,
    phoneNumber?.verified,
  ]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      {authenticated ? (
        content
      ) : (
        <div className="md:mt-[45px] md:shadow">
          <SignUpForm
            {...(isCompany
              ? { companyId: profile?.id }
              : { accountId: profile?.id })}
            type="insight-signup"
          />
        </div>
      )}
    </>
  );
};

export default ProfilePageDiscussions;
